// src/components/StudentModal.tsx
import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// A interface DEVE incluir os campos opcionais
interface Student {
  name: string;
  favoriteQuote: string;
  favoriteSong: string;
  photo: string;
  githubLink?: string; // NOVO
  linkedinLink?: string; // NOVO
}

interface StudentModalProps {
  student: Student | null;
  onClose: () => void;
}

const StudentModal: React.FC<StudentModalProps> = ({ student, onClose }) => {
  // Fechar com ESC e travar scroll
  useEffect(() => {
    if (!student) return;
    
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEsc);
    
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEsc);
    };
  }, [student, onClose]);

  return (
    <AnimatePresence>
      {student && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 md:p-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-md"
              onClick={onClose}
            />

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300, duration: 0.5 }}
              // Melhoria do estilo: Fundo mais dramático e border sutil
              className="relative w-full max-w-4xl flex flex-col md:flex-row bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl border border-gold/30 overflow-hidden my-8" 
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-20 text-white/60 hover:text-white transition-colors h-10 w-10 flex items-center justify-center rounded-full bg-black/20 hover:bg-black/40"
              aria-label="Fechar modal"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>
            
            <div className="w-full md:w-2/5 flex-shrink-0 relative">
              <img
                src={student.photo}
                alt={`Foto de ${student.name}`}
                className="w-full h-56 sm:h-64 md:h-full object-cover"
              />
            </div>

            <div className="w-full md:w-3/5 flex flex-col justify-center p-6 sm:p-8 md:p-12 text-left">
              <h1 
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gold mb-4 tracking-wide"
              >
                {student.name}
              </h1>

              {/* CITAÇÃO MELHORADA COM BORDA LATERAL PARA DESTAQUE */}
              <div className="relative my-4 sm:my-6 p-3 sm:p-4 border-l-4 border-gold bg-gold/5 rounded-r-lg">
                <p className="relative z-10 text-white/90 italic text-lg sm:text-xl lg:text-2xl leading-relaxed">
                  <span className="text-gold text-2xl sm:text-3xl mr-2">"</span>
                    {student.favoriteQuote}
                  <span className="text-gold text-2xl sm:text-3xl ml-2">"</span>
                </p>
              </div>
            
              {/* NOVO: Links Sociais com Ícones no Modal */}
              <div className="flex gap-4 sm:gap-6 mt-3 sm:mt-4 mb-6 sm:mb-8">
                {/* Ícone do LinkedIn */}
                {student.linkedinLink && (
                    <a 
                        href={student.linkedinLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        // Estilo do hover mantido forte para clareza
                        className="text-white/60 hover:text-white hover:bg-[#0A66C2] w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all duration-300"
                        aria-label={`LinkedIn de ${student.name}`}
                    >
                        <i className="fab fa-linkedin-in text-2xl sm:text-4xl"></i>
                    </a>
                )}
                {/* Ícone do GitHub */}
                {student.githubLink && (
                    <a 
                        href={student.githubLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        // Estilo do hover mantido forte para clareza
                        className="text-white/60 hover:text-white hover:bg-[#6e5494] w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all duration-300"
                        aria-label={`GitHub de ${student.name}`}
                    >
                        <i className="fab fa-github text-2xl sm:text-4xl"></i>
                    </a>
                )}
              </div>
              
              <div className="border-t border-gold/20 w-1/4 my-3 sm:my-4"></div>

              <div className="flex items-center gap-3 text-base sm:text-lg text-white/70">
                <svg className="w-6 h-6 flex-shrink-0 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z"></path></svg>
                <span className="text-white/90 font-medium">{student.favoriteSong}</span>
              </div>
            </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default StudentModal;